/**
 * Entité Message - Représente un message dans une conversation
 * Clean Architecture - Domain Layer
 * 
 * Pattern partagé avec raskmote-mobile/src/features/conversation/entities/Message.ts
 */

/**
 * @typedef {'user' | 'assistant' | 'system'} MessageType
 * @typedef {'sending' | 'sent' | 'delivered' | 'error'} MessageStatus
 */

/**
 * @typedef {Object} Message
 * @property {string} id
 * @property {MessageType} type
 * @property {string} content
 * @property {Date} timestamp
 * @property {MessageStatus} status
 * @property {Object} [metadata]
 * @property {number} [metadata.processingTime]
 * @property {string} [metadata.intentType]
 * @property {number} [metadata.confidence]
 */

/**
 * @typedef {Object} UserMessage
 * @property {string} id
 * @property {'user'} type
 * @property {string} content
 * @property {string} originalText
 * @property {Date} timestamp
 * @property {MessageStatus} status
 * @property {Object} [processedIntent]
 */

/**
 * @typedef {Object} AssistantMessage
 * @property {string} id
 * @property {'assistant'} type
 * @property {string} content
 * @property {Date} timestamp
 * @property {MessageStatus} status
 * @property {ActionCard[]} [cards]
 * @property {string[]} [suggestions]
 * @property {boolean} [error]
 */

/**
 * Factory pour créer des messages
 * Aligné avec le pattern mobile
 */
export class MessageFactory {
  /**
   * Crée un message utilisateur
   * @param {string} content - Contenu du message
   * @param {Object} [metadata] - Métadonnées optionnelles
   * @returns {UserMessage}
   */
  static createUserMessage(content, metadata = {}) {
    return {
      id: this.generateId(),
      type: 'user',
      content,
      originalText: content,
      timestamp: new Date(),
      status: 'sending',
      metadata,
    };
  }

  /**
   * Crée un message assistant
   * @param {string} content - Contenu du message
   * @param {ActionCard[]} [cards] - ActionCards optionnelles
   * @param {Object} [metadata] - Métadonnées optionnelles
   * @param {Object} [uiResult] - uiResult optionnel (format ui_result_v1)
   * @returns {AssistantMessage}
   */
  static createAssistantMessage(content, cards = [], metadata = {}, uiResult = undefined) {
    const hasCreateSuggestion = cards?.some(card => card.type === 'create_suggestion');
    
    return {
      id: this.generateId(),
      type: 'assistant',
      content,
      timestamp: new Date(),
      status: 'delivered',
      cards,
      metadata: {
        ...metadata,
        intentType: hasCreateSuggestion ? 'suggestion' : metadata?.intentType,
      },
      ...(uiResult !== undefined && { uiResult }),
    };
  }

  /**
   * Crée un message système
   * @param {string} content - Contenu du message
   * @param {'welcome' | 'error' | 'info' | 'typing'} systemType - Type de message système
   * @returns {Object}
   */
  static createSystemMessage(content, systemType = 'info') {
    return {
      id: this.generateId(),
      type: 'system',
      content,
      systemType,
      timestamp: new Date(),
      status: 'delivered',
    };
  }

  /**
   * Crée un message d'erreur
   * @param {string} errorMessage - Message d'erreur
   * @param {Object} [errorCard] - ActionCard d'erreur optionnelle
   * @returns {AssistantMessage}
   */
  static createErrorMessage(errorMessage, errorCard = null) {
    const cards = errorCard ? [errorCard] : [{
      type: 'error',
      headline: 'Erreur',
      subheadline: errorMessage,
      error: {
        message: errorMessage,
        retryAction: 'retry',
      },
    }];

    return {
      id: this.generateId(),
      type: 'assistant',
      content: errorMessage,
      timestamp: new Date(),
      status: 'delivered',
      cards,
      error: true,
    };
  }

  /**
   * Génère un ID unique pour un message
   * Utilise la même stratégie à 3 niveaux que Conversation.generateId()
   * @returns {string}
   */
  static generateId() {
    // Niveau 1: crypto.randomUUID() si disponible (Chrome 92+, Safari 15.4+)
    if (typeof window !== 'undefined' && 
        window.crypto && 
        typeof window.crypto.randomUUID === 'function') {
      return `msg_${window.crypto.randomUUID()}`;
    }
    
    // Niveau 2: RFC 4122 UUID v4 avec crypto.getRandomValues() (meilleure sécurité)
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const buffer = new Uint8Array(16);
      window.crypto.getRandomValues(buffer);
      
      // Définir les bits de version (4) et variant (RFC 4122)
      buffer[6] = ((buffer[6] & 0x0f) | 0x40); // Version 4
      buffer[8] = ((buffer[8] & 0x3f) | 0x80); // Variant RFC 4122
      
      // Convertir en format UUID
      const hex = Array.from(buffer)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      
      const uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
      return `msg_${uuid}`;
    }
    
    // Niveau 3: Fallback timestamp + Math.random() (moins sécurisé)
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 15);
    return `msg_${timestamp}_${random}`;
  }
}

export default MessageFactory;




